import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { GistFeedReply, GistPostView, GistTab } from '@london-life/contracts';
import { describeError } from '@london-life/errors';
import { api } from '../lib/api';
import { GistPostCard } from './post-card';
import { GistEmpty, type GistNav } from './parts';

// one of Gist's feeds, a page at a time: For you, Trending, Following, a player's posts, your bookmarks
export function GistFeed({
  tab,
  of,
  nav,
  empty,
  onReply,
  top,
  added,
}: {
  tab: GistTab;
  // whose posts (the user tab)
  of?: string;
  nav: GistNav;
  empty: { title: string; body: string };
  // every reply (the composer's tags and the badge come with it)
  onReply?: (reply: GistFeedReply) => void;
  // above the posts (a profile's top)
  top?: (reply: GistFeedReply) => ReactNode;
  // a post just written, put at the top
  added?: GistPostView | null;
}) {
  const [reply, setReply] = useState<GistFeedReply | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const told = useRef(onReply);
  told.current = onReply;
  const load = useCallback(
    (before?: string) => {
      if (before) setMore(true);
      api.gist
        .feed(tab, before, of)
        .then((page) => {
          setFailure(null);
          told.current?.(page);
          setReply((now) => (before && now ? { ...page, posts: [...now.posts, ...page.posts] } : page));
        })
        .catch((error: unknown) => setFailure(describeError(error).message))
        .finally(() => setMore(false));
    },
    [tab, of],
  );
  useEffect(() => {
    setReply(null);
    load();
  }, [load]);
  // a post just written goes to the top of the feeds it belongs in
  useEffect(() => {
    if (added && (tab === 'latest' || tab === 'mine'))
      setReply((now) =>
        now && !now.posts.some((item) => item.id === added.id) ? { ...now, posts: [added, ...now.posts] } : now,
      );
  }, [added, tab]);
  const change = (id: string, next: GistPostView | null) =>
    setReply((now) =>
      now
        ? {
            ...now,
            posts: next
              ? now.posts.map((item) => (item.id === id ? { ...next, registedBy: item.registedBy } : item))
              : now.posts.filter((item) => item.id !== id),
          }
        : now,
    );
  if (failure && !reply)
    return (
      <div className="gist-empty">
        <p>{failure}</p>
        <button type="button" className="gist-link" onClick={() => load()}>
          Try again
        </button>
      </div>
    );
  if (!reply) return <p className="gist-loading">Loading the gist…</p>;
  return (
    <>
      {top?.(reply)}
      {reply.posts.length === 0 && <GistEmpty {...empty} />}
      <ul className="gist-list" aria-label="Posts">
        {reply.posts.map((post) => (
          <GistPostCard
            key={`${post.id}:${post.registedBy ?? ''}`}
            post={post}
            nav={nav}
            onChange={(next) => change(post.id, next)}
            onGone={() => change(post.id, null)}
          />
        ))}
      </ul>
      {reply.next && (
        <button type="button" className="gist-link gist-more-posts" disabled={more} onClick={() => load(reply.next!)}>
          {more ? 'Loading…' : 'Show more'}
        </button>
      )}
    </>
  );
}
